// Central orchestrator for the tool system
// Routes DOM events to the active tool, provides shared raycaster and coordinate pipeline
// See docs/tool-system-architecture.md for full design

import * as THREE from 'three'
import type { GardenScene } from '@/scene/GardenScene'
import type { Tool, ToolContext } from '@/types/editor'
import { worldToEditor, GRID_SNAP } from '@/constants/coordinates'
import { debug } from '@/utils/debug'

export class ToolManager {
  private scene: GardenScene
  private domElement: HTMLElement
  private tools: Map<string, Tool> = new Map()
  private activeTool: Tool | null = null
  private enabled: boolean = false

  // Shared raycaster services (eliminates duplication from ZoneEditHandler + ZoneDrawingHandler)
  private raycaster = new THREE.Raycaster()
  private groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0)
  private intersection = new THREE.Vector3()
  private mouse = new THREE.Vector2()

  // Callbacks
  private toolChangeCallbacks: Array<(name: string) => void> = []

  // Bound event handlers (for cleanup)
  private boundMouseDown: (e: MouseEvent) => void
  private boundMouseMove: (e: MouseEvent) => void
  private boundMouseUp: (e: MouseEvent) => void
  private boundKeyDown: (e: KeyboardEvent) => void
  private boundContextMenu: (e: Event) => void

  constructor(scene: GardenScene, domElement: HTMLElement) {
    this.scene = scene
    this.domElement = domElement

    // Bind handlers once
    this.boundMouseDown = this.handleMouseDown.bind(this)
    this.boundMouseMove = this.handleMouseMove.bind(this)
    this.boundMouseUp = this.handleMouseUp.bind(this)
    this.boundKeyDown = this.handleKeyDown.bind(this)
    this.boundContextMenu = (e: Event) => e.preventDefault()
  }

  // --- Tool registration ---

  registerTool(tool: Tool): void {
    this.tools.set(tool.name, tool)
    debug.log('ToolManager', `Registered tool: ${tool.name}`)
  }

  setActiveTool(name: string): void {
    const tool = this.tools.get(name)
    if (!tool) {
      debug.warn('ToolManager', `Unknown tool: ${name}`)
      return
    }
    if (this.activeTool === tool) return

    this.activeTool?.deactivate()
    this.activeTool = tool
    this.activeTool.activate()
    this.domElement.style.cursor = tool.cursor

    debug.log('ToolManager', `Active tool: ${name}`)

    for (const cb of this.toolChangeCallbacks) {
      cb(name)
    }
  }

  getActiveTool(): Tool | null {
    return this.activeTool
  }

  getActiveToolName(): string {
    return this.activeTool?.name ?? ''
  }


  // --- Cursor ---

  setCursor(cursor: string): void {
    this.domElement.style.cursor = cursor
  }

  // --- Controls integration ---

  setControlsEnabled(enabled: boolean): void {
    this.scene.setControlsEnabled(enabled)
  }

  // --- Lifecycle ---

  enable(): void {
    if (this.enabled) return
    this.enabled = true

    this.domElement.addEventListener('mousedown', this.boundMouseDown)
    this.domElement.addEventListener('mousemove', this.boundMouseMove)
    this.domElement.addEventListener('mouseup', this.boundMouseUp)
    this.domElement.addEventListener('contextmenu', this.boundContextMenu)
    window.addEventListener('keydown', this.boundKeyDown)

    debug.log('ToolManager', 'Enabled')
  }

  disable(): void {
    if (!this.enabled) return
    this.enabled = false

    this.domElement.removeEventListener('mousedown', this.boundMouseDown)
    this.domElement.removeEventListener('mousemove', this.boundMouseMove)
    this.domElement.removeEventListener('mouseup', this.boundMouseUp)
    this.domElement.removeEventListener('contextmenu', this.boundContextMenu)
    window.removeEventListener('keydown', this.boundKeyDown)

    debug.log('ToolManager', 'Disabled')
  }

  dispose(): void {
    this.disable()
    this.activeTool?.deactivate()
    this.activeTool = null

    for (const tool of this.tools.values()) {
      tool.dispose?.()
    }
    this.tools.clear()
    this.toolChangeCallbacks = []

    debug.log('ToolManager', 'Disposed')
  }

  // --- Callbacks ---

  onToolChange(callback: (name: string) => void): () => void {
    this.toolChangeCallbacks.push(callback)
    return () => {
      const index = this.toolChangeCallbacks.indexOf(callback)
      if (index >= 0) this.toolChangeCallbacks.splice(index, 1)
    }
  }

  // --- Coordinate pipeline ---

  /**
   * Build a ToolContext from a mouse event.
   * Converts screen coordinates → world (meters) → editor (snapped dots).
   */
  buildContext(event: MouseEvent): ToolContext {
    const camera = this.scene.getCamera()
    const threeScene = this.scene.getScene()

    // NDC from mouse position relative to container
    const rect = this.domElement.getBoundingClientRect()
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1

    // Raycast against ground plane (Y=0)
    this.raycaster.setFromCamera(this.mouse, camera)
    const hit = this.raycaster.ray.intersectPlane(this.groundPlane, this.intersection)

    let worldPoint: THREE.Vector3 | null = null
    let editorPoint: { x: number; y: number } | null = null

    if (hit) {
      worldPoint = this.intersection.clone()

      // Convert world → editor with grid snap
      const raw = worldToEditor(worldPoint.x, worldPoint.y, worldPoint.z)
      editorPoint = {
        x: Math.round(raw.x / GRID_SNAP) * GRID_SNAP,
        y: Math.round(raw.y / GRID_SNAP) * GRID_SNAP
      }
    }

    return { event, worldPoint, editorPoint, camera, scene: threeScene }
  }

  // --- Event handlers ---

  private handleMouseDown(event: MouseEvent): void {
    if (!this.activeTool) return
    this.activeTool.onMouseDown(this.buildContext(event))
  }

  private handleMouseMove(event: MouseEvent): void {
    if (!this.activeTool) return
    this.activeTool.onMouseMove(this.buildContext(event))
  }

  private handleMouseUp(event: MouseEvent): void {
    if (!this.activeTool) return
    this.activeTool.onMouseUp(this.buildContext(event))
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (!this.activeTool) return
    this.activeTool.onKeyDown(event)
  }
}
